import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { paletteColor } from "../lib/palette";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarCheck,
  Check,
  ChevronRight,
  CircleDollarSign,
  Clock,
  ExternalLink,
  GitBranch,
  ListChecks,
  Mail,
  MessageSquare,
  Paperclip,
  Pencil,
  Phone,
  Plus,
  Target,
  Trash2,
  TriangleAlert,
  Undo2,
  Users,
  X,
} from "lucide-react";
import { useStore } from "../lib/store";
import { showFeedback } from "../lib/feedback";
import { formatCurrency } from "../lib/utils";
import type { ActivityEvent, Priority } from "../lib/types";
import {
  HEAT_EXPLANATION,
  buildRows,
  heatColor,
  priorityLabel,
  priorityTone,
  sourceColor,
} from "../features/leads/list";
import {
  PROFILE_TABS,
  daysInFunnel,
  isProfileTab,
  pendingTasksOf,
  quietDays,
  situation,
  stageJourney,
  timeline,
  undoPlan,
  type ProfileTabId,
} from "../features/leads/profile";
import { formFieldsOf } from "../features/leads/form";
import { civilDate } from "../features/notifications/feed";
import {
  invoiceBalanceCents,
  invoiceStatus,
  leadReceivedCents,
  INVOICE_STATUS_LABEL,
  INVOICE_HORIZON_DAYS,
  addDays,
  type FinanceContract,
} from "../features/finance/contracts";
import { formatCents } from "../features/finance/format";
import { NewTaskDialog } from "../features/tasks/NewTaskDialog";
import { describeDue } from "../features/tasks/board";
import { TIME_ZONE } from "../features/tasks/civil-time";
import { PageHead } from "../ui/PageHead";
import { Tabs } from "../ui/Tabs";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import type { Meeting } from "../features/meetings/contracts";
import { Avatar } from "../ui/Avatar";
import { avatarInitials } from "../ui/avatar-identity";
import { Pill } from "../ui/Pill";
import { EmptyState } from "../ui/EmptyState";
import { HelpPopover } from "../ui/HelpPopover";
import { appPath } from "../app/routes";
import {
  CancelContractDialog,
  ConfirmDialog,
  ContactDialog,
  ContractDialog,
  ExtraInvoiceDialog,
  EditLeadDialog,
  LinkDialog,
  LostDialog,
  CancelMeetingDialog,
  MeetingDialog,
  NoteDialog,
  ReceiptDialog,
} from "./PerfilDialogs";
import "./perfil.css";

/**
 * Perfil do lead.
 *
 * Três colunas, como no protótipo: identidade à esquerda, jornada e atividade
 * ao centro, contexto à direita. O que muda em relação ao protótipo é de
 * domínio, não de composição:
 *
 * - a nota fictícia de IA virou o Calor, com a mesma fórmula da lista e a
 *   explicação publicada na ajuda (AGENTS §10);
 * - "alterar funil" não existe: um lead pertence a um funil por todo o
 *   histórico, e trabalhar a mesma empresa em outro funil é outro lead (§7.2);
 * - a permanência em cada etapa só aparece quando o domínio a registrou;
 * - desfazer diz antes se vai reverter o estado ou apenas apagar o registro
 *   (§7.4).
 */

function Panel({
  title,
  icon,
  tone,
  count,
  sub,
  action,
  className,
  children,
}: {
  title: string;
  icon: ReactNode;
  tone?: string;
  count?: number;
  sub?: ReactNode;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`cx-card cx-pf-panel${className ? ` ${className}` : ""}`}>
      <header>
        <div>
          <h2>
            <span
              className="cx-pf-ico"
              style={
                tone ? ({ "--cx-pf-tone": tone } as CSSProperties) : undefined
              }
              aria-hidden="true"
            >
              {icon}
            </span>
            {title}
            {count !== undefined && count > 0 && (
              <span className="cx-pf-count">{count}</span>
            )}
          </h2>
          {sub !== undefined && <p>{sub}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/**
 * Ficha de dados em linhas: rótulo à esquerda, valor à direita, uma divisória
 * por linha. É a leitura do protótipo — em uma coluna de 300px, duas colunas de
 * fatos quebram o e-mail no meio e empurram o resto para fora do card.
 */
function Details({
  items,
}: {
  items: Array<{ label: ReactNode; value: ReactNode; numeric?: boolean }>;
}) {
  return (
    <dl className="cx-pf-details">
      {items.map((item, index) => (
        <div className="cx-pf-detail" key={index}>
          <dt>{item.label}</dt>
          <dd className={item.numeric ? "cx-data" : undefined}>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

const dayLabel = (iso: string) => civilDate(iso).split("-").reverse().join("/");

export function PerfilPage() {
  const store = useStore();
  const navigate = useNavigate();
  const { leadId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Entrar no cadastro leva o foco ao nome, como toda mudança de rota (§17).
  useEffect(() => {
    titleRef.current?.focus();
  }, [leadId]);
  const now = useMemo(() => new Date().toISOString(), []);

  const [editing, setEditing] = useState(false);
  const [noting, setNoting] = useState(false);
  const [tasking, setTasking] = useState(false);
  const [linking, setLinking] = useState(false);
  const [meeting, setMeeting] = useState(false);
  /** Reunião aberta para edição, e a que aguarda motivo de cancelamento. */
  const [editandoReuniao, setEditandoReuniao] = useState<Meeting>();
  const [cancelandoReuniao, setCancelandoReuniao] = useState<Meeting>();
  const [contracting, setContracting] = useState(false);
  /** Contrato que vai receber um extra, e o que aguarda encerramento. */
  const [extraDe, setExtraDe] = useState<FinanceContract>();
  const [encerrando, setEncerrando] = useState<FinanceContract>();
  const [receiptFor, setReceiptFor] = useState<string | null>(null);
  const [contactFor, setContactFor] = useState<{ id?: string } | null>(null);
  const [losing, setLosing] = useState(false);
  const [trashing, setTrashing] = useState(false);
  const [undoing, setUndoing] = useState<ActivityEvent | null>(null);

  const lead = store.leads.find((item) => item.id === leadId);
  const tabParam = params.get("aba");
  const tab: ProfileTabId = isProfileTab(tabParam) ? tabParam : "historico";

  const rows = useMemo(
    () =>
      buildRows({
        leads: store.leads,
        memberships: store.memberships,
        stages: store.stages,
        funnels: store.funnels,
        tags: store.tags,
        tasks: store.tasks,
        leadFields: store.leadFields,
        leadFieldValues: store.leadFieldValues,
        now,
      }),
    [
      now,
      store.funnels,
      store.leadFieldValues,
      store.leadFields,
      store.leads,
      store.memberships,
      store.stages,
      store.tags,
      store.tasks,
    ],
  );
  const row = rows.find((item) => item.lead.id === leadId);

  if (!lead || !row)
    return (
      <div className="cx-perfil">
        <section className="cx-work">
          <PageHead title="Cadastro não encontrado" />
          <EmptyState
            title="Este cadastro não está disponível"
            description="Ele pode ter sido excluído permanentemente ou nunca ter existido neste ambiente."
            action={
              <Button onClick={() => navigate(appPath("leads"))}>
                Voltar para Leads
              </Button>
            }
          />
        </section>
      </div>
    );

  const membership = row.membership;
  const funnelStages = membership
    ? store.stages.filter(
        (stage) => stage.funnelId === membership.funnelId && !stage.archived,
      )
    : [];
  const steps = stageJourney({
    stages: funnelStages,
    membership,
    events: store.events,
    now,
  });
  const events = timeline(store.events, lead.id);
  const notes = events.filter((entry) => entry.event.type === "note_added");
  const leadTasks = store.tasks.filter((task) => task.leadId === lead.id);
  const pending = pendingTasksOf(store.tasks, lead.id);
  const quiet = quietDays(lead, now);
  const contracts = (store.contracts ?? []).filter(
    (contract) => contract.leadId === lead.id,
  );
  const invoices = (store.invoices ?? []).filter(
    (invoice) => invoice.leadId === lead.id,
  );
  const receipts = store.receipts ?? [];
  const meetings = (store.meetings ?? []).filter(
    (item) => item.leadId === lead.id,
  );
  const attachments = store.attachments.filter(
    (item) => item.leadId === lead.id,
  );
  const links = store.links.filter((item) => item.leadId === lead.id);
  const contacts = store.contacts.filter(
    (contact) =>
      (lead.contactIds ?? []).includes(contact.id) ||
      contact.id === lead.primaryContactId,
  );
  const company = lead.companyId
    ? store.companies.find((item) => item.id === lead.companyId)
    : undefined;
  // Contato pertence a uma empresa e depende da fundação de identidade (§27.2);
  // sem as duas coisas o controle aparece desabilitado com o motivo (§18).
  const contactsWritable = Boolean(company) && store.identityFoundationAvailable;
  const fields = membership
    ? formFieldsOf(store.leadFields, membership.funnelId)
    : [];
  const fieldValue = (fieldId: string) =>
    store.leadFieldValues.find(
      (value) => value.leadId === lead.id && value.fieldId === fieldId,
    )?.value;
  // O diagnóstico do protótipo é a trinca dor/objeção/entrega. Aqui ela é
  // campo do funil, não coluna fixa, então o card mostra o que o funil define
  // com essas chaves e o resto dos campos desce para a ficha de dados.
  const DIAGNOSIS_KEYS = ["biggest_pain", "biggest_objection", "best_overdelivery"];
  const diagFields = fields.filter((field) => DIAGNOSIS_KEYS.includes(field.key));
  const extraFields = fields.filter((field) => !DIAGNOSIS_KEYS.includes(field.key));
  // Segmento, origem e função já são campos-base do lead e aparecem na ficha
  // com o valor real; repeti-los como catálogo daria a mesma informação duas
  // vezes, uma delas sempre vazia.
  const BASE_FIELD_KEYS = ["segment", "source", "contact_role"];
  const classifications = (store.classificationDefinitions ?? [])
    .filter(
      (definition) =>
        !definition.archived && !BASE_FIELD_KEYS.includes(definition.key),
    )
    .sort((left, right) => left.order - right.order)
    .map((definition) => {
      const chosen = (store.leadClassificationValues ?? []).find(
        (value) =>
          value.leadId === lead.id && value.definitionId === definition.id,
      );
      const option = chosen
        ? (store.classificationOptions ?? []).find(
            (item) => item.id === chosen.optionId,
          )
        : undefined;
      return { name: definition.name, value: option?.name };
    });
  const lossReason = membership?.lossReasonId
    ? store.lossReasons.find((reason) => reason.id === membership.lossReasonId)
    : undefined;
  const state = situation({
    status: membership?.status,
    quietDays: quiet,
    pendingTasks: pending.length,
    isClient: Boolean(lead.clientSince),
  });

  const setTab = (next: string) => {
    const nextParams = new URLSearchParams(params);
    if (next === "historico") nextParams.delete("aba");
    else nextParams.set("aba", next);
    setParams(nextParams, { replace: true });
  };

  const quietLabel =
    quiet === null
      ? "Nenhuma interação registrada"
      : quiet === 0
        ? "Última interação hoje"
        : `Última interação há ${quiet} ${quiet === 1 ? "dia" : "dias"}`;
  const stale = quiet !== null && quiet >= 7;

  return (
    <div className="cx-perfil">
      <section className="cx-work">
        <div className="cx-pf-top">
          <Button
            variant="ghost"
            size="compact"
            className="cx-back"
            icon={<ArrowLeft size={14} aria-hidden="true" />}
            onClick={() => navigate(appPath(lead.clientSince ? "clientes" : "leads"))}
          >
            Voltar
          </Button>
          {lead.clientSince && <Pill>Cliente</Pill>}
        </div>

        <div className="cx-pf-layout">
          {/* --- Identidade ------------------------------------------------- */}
          <div className="cx-pf-col">
            <section className="cx-card cx-pf-identity">
              <div className="cx-pf-head">
                <Avatar name={lead.name} photoUrl={lead.photoUrl} size="lg" />
                <div>
                  {/* O nome do cadastro é o título da superfície e recebe o
                      foco na entrada da rota (§17); o protótipo o mantém aqui,
                      dentro da ficha, e não em um cabeçalho de página. */}
                  <h1 ref={titleRef} tabIndex={-1}>
                    {lead.name}
                  </h1>
                  <p className="cx-pf-co">
                    <strong>{lead.company}</strong>
                    <span>{lead.role}</span>
                  </p>
                </div>
              </div>

              <div className="cx-pf-pills">
                <Pill
                  tone={
                    membership?.status === "lost"
                      ? "crit"
                      : membership?.status === "won"
                        ? "good"
                        : "accent"
                  }
                  dot
                >
                  {row.statusLabel}
                </Pill>
                <span
                  className="cx-lead-priority"
                  style={
                    {
                      "--cx-tone": priorityTone(lead.priority),
                    } as CSSProperties
                  }
                >
                  <i aria-hidden="true" />
                  {priorityLabel(lead.priority as Priority)}
                </span>
                {row.tagNames.map((name) => (
                  <Pill key={name}>{name}</Pill>
                ))}
              </div>

              <div className="cx-pf-tiles">
                <div className="cx-pf-tile" data-featured="true">
                  <span className="cx-pf-tile-label">
                    {lead.clientSince ? "Valor do negócio" : "Valor potencial"}
                    <HelpPopover
                      about="Sobre o valor"
                      items={[
                        {
                          term: "O que é",
                          description:
                            "Valor cadastral desta negociação. Não corresponde à receita recebida nem ao LTV.",
                        },
                      ]}
                    />
                  </span>
                  <b className="cx-data">
                    {formatCurrency(lead.potentialValue)}
                  </b>
                </div>
                <div
                  className="cx-pf-tile"
                  style={
                    {
                      "--cx-pf-score": heatColor(row.heat),
                      "--cx-pf-score-width": `${row.heat}%`,
                    } as CSSProperties
                  }
                >
                  <span className="cx-pf-tile-label">
                    AI Score
                    <HelpPopover
                      about="Como o AI Score é calculado"
                      items={HEAT_EXPLANATION}
                    />
                  </span>
                  <div className="cx-pf-tile-score">
                    <b className="cx-data cx-pf-score-num">{row.heat}</b>
                    <span className="cx-pf-meter" aria-hidden="true">
                      <i />
                    </span>
                  </div>
                </div>
              </div>

              <div className="cx-pf-quick">
                <Button
                  size="compact"
                  icon={<MessageSquare size={14} aria-hidden="true" />}
                  disabled={!store.emailInboxAvailable}
                  title={
                    store.emailInboxAvailable
                      ? undefined
                      : "A caixa de e-mail não está habilitada neste ambiente."
                  }
                  onClick={() => navigate(appPath("mensagens"))}
                >
                  Mensagem
                </Button>
                {lead.email ? (
                  <a
                    className="cx-btn"
                    data-variant="secondary"
                    data-size="compact"
                    href={`mailto:${lead.email}`}
                  >
                    <Mail size={14} aria-hidden="true" />
                    E-mail
                  </a>
                ) : (
                  <Button
                    size="compact"
                    disabled
                    title="Sem e-mail informado"
                    icon={<Mail size={14} aria-hidden="true" />}
                  >
                    E-mail
                  </Button>
                )}
                {lead.phone ? (
                  <a
                    className="cx-btn"
                    data-variant="secondary"
                    data-size="compact"
                    href={`tel:${lead.phone.replace(/[^\d+]/g, "")}`}
                  >
                    <Phone size={14} aria-hidden="true" />
                    Ligar
                  </a>
                ) : (
                  <Button
                    size="compact"
                    disabled
                    title="Sem telefone informado"
                    icon={<Phone size={14} aria-hidden="true" />}
                  >
                    Ligar
                  </Button>
                )}
                <Button
                  size="compact"
                  icon={<Plus size={14} aria-hidden="true" />}
                  onClick={() => setTasking(true)}
                >
                  Tarefa
                </Button>
              </div>

              <div className="cx-pf-cta">
                {membership?.status === "active" ? (
                  <>
                    <Button
                      variant="primary"
                      onClick={() => {
                        store.setMembershipOutcome(membership.id, "won");
                        showFeedback("Negociação marcada como ganha.", "success");
                      }}
                    >
                      Marcar ganho
                    </Button>
                    <Button onClick={() => setLosing(true)}>Marcar perdido</Button>
                  </>
                ) : membership ? (
                  <Button
                    variant="primary"
                    className="cx-pf-wide"
                    onClick={() => {
                      store.setMembershipOutcome(membership.id, "active");
                      showFeedback("Negociação reaberta em um novo ciclo.", "success");
                    }}
                  >
                    Reabrir negociação
                  </Button>
                ) : null}
                <Button variant="ghost" className="cx-pf-wide" onClick={() => setEditing(true)}>
                  Editar dados
                </Button>
                {lead.clientSince && (
                  <Button
                    variant="ghost"
                    className="cx-pf-wide"
                    title="Prévia interna do que o cliente veria; não publica nada."
                    onClick={() => navigate(`${appPath("portal")}/${lead.id}`)}
                  >
                    Visualizar como cliente
                  </Button>
                )}
              </div>

              <p
                className="cx-pf-quiet"
                data-stale={stale ? "true" : undefined}
                data-unknown={quiet === null ? "true" : undefined}
              >
                <i aria-hidden="true" />
                {quietLabel}
              </p>

              <Details
                items={[
                  {
                    label: "E-mail",
                    value: lead.email ? (
                      <a href={`mailto:${lead.email}`}>{lead.email}</a>
                    ) : (
                      "Não informado"
                    ),
                  },
                  {
                    label: "Telefone",
                    value: lead.phone ? (
                      <a href={`tel:${lead.phone.replace(/[^\d+]/g, "")}`}>
                        {lead.phone}
                      </a>
                    ) : (
                      "Não informado"
                    ),
                  },
                  {
                    label: "Origem",
                    value: (
                      <>
                        <span
                          className="cx-pf-src"
                          style={{ background: sourceColor(lead.source) }}
                          aria-hidden="true"
                        />
                        {lead.source || "Não informada"}
                      </>
                    ),
                  },
                  {
                    label: "No funil há",
                    numeric: true,
                    value: membership
                      ? `${daysInFunnel(lead, now)} ${daysInFunnel(lead, now) === 1 ? "dia" : "dias"}`
                      : "—",
                  },
                  ...classifications.map((item) => ({
                    label: item.name,
                    value: item.value ?? "Não informada",
                  })),
                  ...extraFields.map((field) => ({
                    label: field.name,
                    value: String(fieldValue(field.id) ?? "") || "Não informado",
                  })),
                ]}
              />

              <div className="cx-pf-foot">
                <Button
                  variant="ghost"
                  size="compact"
                  icon={<Trash2 size={14} aria-hidden="true" />}
                  onClick={() => setTrashing(true)}
                >
                  Mover para lixeira
                </Button>
              </div>
            </section>
          </div>

          {/* --- Jornada e atividade ---------------------------------------- */}
          <div className="cx-pf-col">
            <Panel
              title="Jornada no funil"
              icon={<GitBranch size={14} />}
              tone="var(--accent)"
              sub={
                <>
                  {membership ? row.funnelName : "Sem funil vinculado"}
                  {membership && steps.length
                    ? ` · etapa ${steps.findIndex((step) => step.state === "current") + 1} de ${steps.length}`
                    : ""}
                  {membership
                    ? ` · ${daysInFunnel(lead, now)} ${daysInFunnel(lead, now) === 1 ? "dia" : "dias"} no funil`
                    : ""}
                </>
              }
              action={
                <Button
                  size="compact"
                  disabled
                  title="Um lead pertence a um funil por todo o histórico. Para trabalhar a mesma empresa em outro funil, o sistema cria outro lead, sem transferir jornada nem desfecho."
                >
                  Alterar funil
                </Button>
              }
            >
              {steps.length ? (
                <div
                  className="cx-pf-steps"
                  role="group"
                  aria-label="Etapas do funil"
                >
                  {steps.map((step) => (
                    <button
                      key={step.stage.id}
                      type="button"
                      className="cx-pf-step"
                      data-state={step.state}
                      style={{ "--cx-step": paletteColor(step.stage.color) } as CSSProperties}
                      aria-current={
                        step.state === "current" ? "step" : undefined
                      }
                      disabled={!membership || membership.status !== "active"}
                      title={
                        membership?.status === "active"
                          ? undefined
                          : "A participação está encerrada; reabra a negociação para movimentar."
                      }
                      onClick={() =>
                        membership &&
                        store.moveMembership(membership.id, step.stage.id)
                      }
                      aria-label={`${step.stage.name}${
                        step.state === "current"
                          ? " (etapa atual)"
                          : step.state === "past"
                            ? " (concluída)"
                            : ""
                      }`}
                    >
                      <span className="cx-pf-step-ico" aria-hidden="true">
                        {step.state === "past" ? (
                          <Check size={11} strokeWidth={3} />
                        ) : step.state === "current" ? (
                          <i />
                        ) : null}
                      </span>
                      <span className="cx-pf-step-name">{step.stage.name}</span>
                      {step.state !== "future" && (
                        <span className="cx-pf-step-days">
                          {step.durationLabel ?? "—"}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="cx-note">
                  Este cadastro não tem participação em nenhum funil.
                </p>
              )}
              {lossReason && (
                <p className="cx-pf-loss">
                  Motivo de perda: <strong>{lossReason.name}</strong>
                </p>
              )}
            </Panel>

            <Panel
              title="Próximas ações"
              icon={<CalendarCheck size={14} />}
              tone="var(--warn)"
              count={pending.length}
              sub={`${quietLabel}${pending.length ? "" : " · nenhuma tarefa pendente"}`}
              action={
                <Button
                  size="compact"
                  icon={<Plus size={14} aria-hidden="true" />}
                  onClick={() => setTasking(true)}
                >
                  Tarefa
                </Button>
              }
            >
              {pending.length ? (
                <>
                  {pending.slice(0, 3).map((task) => {
                    const due = describeDue(task, {
                      view: "todas",
                      now,
                      today: civilDate(now),
                      timeZone: TIME_ZONE,
                    });
                    return (
                      <button
                        key={task.id}
                        type="button"
                        className="cx-pf-task"
                        data-late={due.late ? "true" : undefined}
                        onClick={() => setTab("tarefas")}
                      >
                        <span className="cx-pf-task-ico" aria-hidden="true">
                          {due.late ? (
                            <TriangleAlert size={15} />
                          ) : (
                            <ListChecks size={15} />
                          )}
                        </span>
                        <span className="cx-pf-task-body">
                          <strong>{task.description}</strong>
                          <small className="cx-data">
                            {`${due.late ? "Atrasada · " : ""}${due.label} · ${due.time}`}
                          </small>
                        </span>
                        <ChevronRight
                          size={14}
                          className="cx-pf-task-go"
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                  {pending.length > 3 && (
                    <p className="cx-pf-more">
                      <Button
                        variant="text"
                        size="compact"
                        onClick={() => setTab("tarefas")}
                      >
                        {`Ver todas as ${pending.length} tarefas`}
                      </Button>
                    </p>
                  )}
                </>
              ) : (
                <div className="cx-pf-situation" data-tone={state.tone}>
                  <span className="cx-pf-sig" aria-hidden="true">
                    {state.tone === "crit" ? (
                      <TriangleAlert size={16} />
                    ) : state.tone === "warn" ? (
                      <Clock size={16} />
                    ) : state.tone === "good" ? (
                      <Check size={16} />
                    ) : (
                      <Target size={16} />
                    )}
                  </span>
                  <div>
                    <strong>{state.title}</strong>
                    <p>{state.text}</p>
                  </div>
                  <HelpPopover
                    about="Sobre a sugestão"
                    items={[
                      {
                        term: "Origem",
                        description:
                          "Desfecho da participação, dias desde a última interação real e tarefas pendentes deste cadastro.",
                      },
                      {
                        term: "O que não é",
                        description:
                          "Não é análise de IA e não cria tarefa: é leitura determinística do que já está registrado.",
                      },
                    ]}
                  />
                </div>
              )}
            </Panel>

            <section className="cx-card cx-pf-tabs">
              <Tabs
                label="Seções do cadastro"
                value={tab}
                onChange={setTab}
                items={PROFILE_TABS.map((item) => {
                  const count =
                    item.id === "historico"
                      ? events.length
                      : item.id === "notas"
                        ? notes.length
                        : item.id === "tarefas"
                          ? leadTasks.length
                          : contracts.length;
                  // Zero não vira contador: o protótipo só marca a aba que tem
                  // algo, e uma fileira de zeros não informa nada.
                  return { id: item.id, label: item.label, count: count || undefined };
                })}
              />

              {tab === "historico" && (
                <>
                <div className="cx-pf-tabbar cx-pf-subhead">
                  <h2>Linha do tempo</h2>
                  <span className="cx-note">
                    {`${events.length} ${events.length === 1 ? "registro" : "registros"}`}
                  </span>
                </div>
                <ol className="cx-pf-timeline">
                  {events.map((entry) => (
                    <li key={entry.event.id} data-tone={entry.tone}>
                      <div>
                        <strong>{entry.event.title}</strong>
                        {entry.event.description && (
                          <p>{entry.event.description}</p>
                        )}
                        <small>
                          {entry.label} · {dayLabel(entry.event.occurredAt)}
                          {entry.event.durationLabel
                            ? ` · ${entry.event.durationLabel}`
                            : ""}
                        </small>
                      </div>
                      {entry.removable && (
                        <IconButton
                          size="compact"
                          variant="bare"
                          label={`Desfazer ${entry.event.title}`}
                          icon={<Undo2 size={14} aria-hidden="true" />}
                          onClick={() => setUndoing(entry.event)}
                        />
                      )}
                    </li>
                  ))}
                  {events.length === 0 && (
                    <li>
                      <EmptyState
                        title="Nenhum registro ainda"
                        description="A jornada aparece aqui conforme o cadastro avança."
                      />
                    </li>
                  )}
                </ol>
                </>
              )}

              {tab === "notas" && (
                <>
                  <div className="cx-pf-tabbar">
                    <span className="cx-note">
                      Contexto interno para a próxima conversa.
                    </span>
                    <Button
                      size="compact"
                      icon={<Plus size={14} aria-hidden="true" />}
                      onClick={() => setNoting(true)}
                    >
                      Nota
                    </Button>
                  </div>
                  {notes.length ? (
                    <ul className="cx-pf-notes">
                      {notes.map((entry) => (
                        <li key={entry.event.id}>
                          <p>{entry.event.description || entry.event.title}</p>
                          <small>{dayLabel(entry.event.occurredAt)}</small>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <EmptyState
                      title="Nenhuma nota ainda"
                      description="Registre o que importa para a próxima conversa."
                      action={
                        <Button onClick={() => setNoting(true)}>
                          Escrever nota
                        </Button>
                      }
                    />
                  )}
                </>
              )}

              {tab === "tarefas" && (
                <>
                  <div className="cx-pf-tabbar">
                    <span className="cx-note">
                      Todas as ações vinculadas a este cadastro.
                    </span>
                    <Button
                      size="compact"
                      icon={<Plus size={14} aria-hidden="true" />}
                      onClick={() => setTasking(true)}
                    >
                      Tarefa
                    </Button>
                  </div>
                  {leadTasks.length ? (
                    <ul className="cx-pf-tasks">
                      {leadTasks.map((task) => {
                        const due = describeDue(task, {
                          view: "todas",
                          now,
                          today: civilDate(now),
                          timeZone: TIME_ZONE,
                        });
                        return (
                          <li
                            key={task.id}
                            data-done={task.status === "done" || undefined}
                          >
                            <span>
                              <strong>{task.description}</strong>
                              <small>{due.label}</small>
                            </span>
                            <Button
                              size="compact"
                              onClick={() => {
                                void (task.status === "done"
                                  ? store.reopenTask(task.id)
                                  : store.completeTask(task.id));
                              }}
                            >
                              {task.status === "done" ? "Reabrir" : "Concluir"}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <EmptyState
                      title="Nenhuma tarefa"
                      description="Marque o próximo passo para não perder o cadastro de vista."
                      action={
                        <Button onClick={() => setTasking(true)}>
                          Criar tarefa
                        </Button>
                      }
                    />
                  )}

                  {/* As reuniões vivem dentro de Tarefas, como no protótipo:
                      são o próximo passo combinado, não uma seção à parte.
                      Confirmar que aconteceu é o que conta como interação
                      real (§7.7). */}
                  <div className="cx-pf-tabbar cx-pf-subhead">
                    <h2>Reuniões</h2>
                    <Button
                      size="compact"
                      icon={<Plus size={14} aria-hidden="true" />}
                      onClick={() => setMeeting(true)}
                    >
                      Reunião
                    </Button>
                  </div>
                  {meetings.length ? (
                    <ul className="cx-pf-tasks">
                      {meetings.map((item) => (
                        <li
                          key={item.id}
                          data-done={Boolean(item.heldAt) || undefined}
                        >
                          <span>
                            <strong>{item.title}</strong>
                            <small>
                              {dayLabel(item.startsAt)} ·{" "}
                              {item.cancelledAt
                                ? "Cancelada"
                                : item.heldAt
                                  ? "Realizada"
                                  : `${item.durationMinutes} min`}
                            </small>
                          </span>
                          {!item.heldAt && !item.cancelledAt && (
                            <span className="cx-pf-meeting-actions">
                              <Button
                                size="compact"
                                onClick={() => {
                                  void store.holdMeeting({ meetingId: item.id });
                                }}
                              >
                                Marcar realizada
                              </Button>
                              <IconButton
                                size="compact"
                                variant="bare"
                                label={`Editar ${item.title}`}
                                icon={<Pencil size={14} aria-hidden="true" />}
                                onClick={() => setEditandoReuniao(item)}
                              />
                              <IconButton
                                size="compact"
                                variant="bare"
                                label={`Cancelar ${item.title}`}
                                icon={<X size={14} aria-hidden="true" />}
                                onClick={() => setCancelandoReuniao(item)}
                              />
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <EmptyState
                      title="Nenhuma reunião"
                      description="Agende a conversa e registre a realização quando ela acontecer."
                      action={
                        <Button onClick={() => setMeeting(true)}>
                          Agendar reunião
                        </Button>
                      }
                    />
                  )}
                </>
              )}

              {tab === "contratos" && (
                <>
                  <div className="cx-pf-tabbar">
                    <span className="cx-note">
                      O primeiro recebimento fecha a venda e converte o lead em
                      cliente.
                    </span>
                    <Button
                      size="compact"
                      icon={<Plus size={14} aria-hidden="true" />}
                      onClick={() => setContracting(true)}
                    >
                      Contrato
                    </Button>
                  </div>
                  {contracts.length ? (
                    <ul className="cx-pf-contracts">
                      {contracts.map((contract) => {
                        const own = invoices
                          .filter(
                            (invoice) => invoice.contractId === contract.id,
                          )
                          .sort((a, b) => a.dueOn.localeCompare(b.dueOn));
                        return (
                          <li key={contract.id}>
                            <div className="cx-pf-contract-head">
                              <span>
                                <strong>{contract.description}</strong>
                                <small>
                                  {contract.kind === "monthly"
                                    ? "Mensal"
                                    : "Avulso"}{" "}
                                  · {formatCents(contract.amountCents)} · início{" "}
                                  {contract.startOn
                                    .split("-")
                                    .reverse()
                                    .join("/")}
                                </small>
                              </span>
                              <Pill
                                tone={
                                  contract.cancelledOn
                                    ? "neutral"
                                    : contract.closedOn
                                      ? "good"
                                      : "warn"
                                }
                                dot
                              >
                                {contract.cancelledOn
                                  ? "Encerrado"
                                  : contract.closedOn
                                    ? "Ativo"
                                    : "Aguardando 1º recebimento"}
                              </Pill>
                            </div>
                            <ul className="cx-pf-invoices">
                              {own.map((invoice) => {
                                const status = invoiceStatus(
                                  invoice,
                                  receipts,
                                  civilDate(now),
                                );
                                const balance = invoiceBalanceCents(
                                  invoice,
                                  receipts,
                                );
                                return (
                                  <li key={invoice.id}>
                                    <span>
                                      {invoice.description}
                                      <small>
                                        vence{" "}
                                        {invoice.dueOn
                                          .split("-")
                                          .reverse()
                                          .join("/")}{" "}
                                        · {INVOICE_STATUS_LABEL[status]}
                                      </small>
                                    </span>
                                    <b className="cx-data">
                                      {formatCents(invoice.amountCents)}
                                    </b>
                                    <Button
                                      size="compact"
                                      disabled={
                                        balance <= 0 ||
                                        Boolean(invoice.cancelledAt)
                                      }
                                      title={
                                        balance <= 0
                                          ? "Esta parcela já está quitada."
                                          : invoice.cancelledAt
                                            ? "Parcela cancelada pelo encerramento do contrato."
                                            : undefined
                                      }
                                      onClick={() => setReceiptFor(invoice.id)}
                                    >
                                      Receber
                                    </Button>
                                  </li>
                                );
                              })}
                            </ul>
                            <div className="cx-pf-contract-actions">
                              <Button
                                size="compact"
                                variant="text"
                                onClick={() => setExtraDe(contract)}
                                disabled={Boolean(contract.cancelledOn)}
                                title={
                                  contract.cancelledOn
                                    ? "Contrato encerrado não recebe novas cobranças."
                                    : undefined
                                }
                              >
                                Adicionar extra
                              </Button>
                              {contract.kind === "monthly" && !contract.cancelledOn && (
                                <>
                                  {/* A série é gerada por horizonte, não até o
                                      infinito: quando ele se esgota, estender é
                                      um comando visível, nunca efeito de abrir
                                      a tela. */}
                                  <Button
                                    size="compact"
                                    variant="text"
                                    onClick={async () => {
                                      const result = await store.generateContractInvoices({
                                        contractId: contract.id,
                                        until: addDays(civilDate(now), INVOICE_HORIZON_DAYS),
                                      });
                                      if (result.ok)
                                        showFeedback(
                                          "Parcelas do próximo horizonte geradas.",
                                          "success",
                                        );
                                    }}
                                  >
                                    Gerar próximas parcelas
                                  </Button>
                                  <Button
                                    size="compact"
                                    variant="text"
                                    onClick={() => setEncerrando(contract)}
                                  >
                                    Encerrar contrato
                                  </Button>
                                </>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <EmptyState
                      title="Nenhum contrato"
                      description="Registre o contrato para acompanhar parcelas, recebimentos e receita."
                      action={
                        <Button onClick={() => setContracting(true)}>
                          Criar contrato
                        </Button>
                      }
                    />
                  )}
                </>
              )}

            </section>
          </div>

          {/* --- Contexto ---------------------------------------------------- */}
          <div className="cx-pf-col">
            <section className="cx-card cx-pf-company">
              <div className="cx-pf-cohead">
                <span className="cx-pf-cologo" aria-hidden="true">
                  {avatarInitials(company?.tradeName || company?.legalName || lead.company)}
                </span>
                <div>
                  <h2>{company?.tradeName || company?.legalName || lead.company}</h2>
                  <p>
                    {[company?.segment || lead.segment, row.city]
                      .filter(Boolean)
                      .join(" · ") || "Segmento e cidade não informados"}
                  </p>
                </div>
              </div>
              <Details
                items={[
                  {
                    label: "Site",
                    value: lead.site ? (
                      <a href={lead.site} target="_blank" rel="noreferrer noopener">
                        {lead.site}
                      </a>
                    ) : (
                      "Não informado"
                    ),
                  },
                  {
                    label: "Instagram",
                    value: lead.instagram ? (
                      <a
                        href={`https://instagram.com/${lead.instagram.replace(/^@/, "")}`}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {lead.instagram}
                      </a>
                    ) : (
                      "Não informado"
                    ),
                  },
                  {
                    label: "Segmento",
                    value: company?.segment || lead.segment || "Não informado",
                  },
                  {
                    label: "Cidade",
                    value:
                      [company?.city, company?.state].filter(Boolean).join(" / ") ||
                      row.city ||
                      "Não informada",
                  },
                ]}
              />
            </section>

            <Panel
              title="Contratos"
              icon={<CircleDollarSign size={14} />}
              tone="var(--good)"
              count={contracts.length}
              action={
                <Button
                  size="compact"
                  variant="text"
                  onClick={() => setTab("contratos")}
                >
                  {contracts.length ? "Ver tudo" : "+ Contrato"}
                </Button>
              }
            >
              <dl className="cx-pf-ltv">
                <dt>
                  LTV acumulado
                  <HelpPopover
                    about="Sobre o LTV"
                    items={[
                      {
                        term: "O que entra",
                        description:
                          "Soma de todos os recebimentos registrados deste cadastro, inclusive os anteriores à conversão.",
                      },
                    ]}
                  />
                </dt>
                <dd className="cx-data">
                  {formatCents(leadReceivedCents(receipts, lead.id))}
                </dd>
              </dl>
              {contracts.length ? (
                <ul className="cx-pf-contract-list">
                  {contracts.slice(0, 3).map((contract) => (
                    <li key={contract.id}>
                      <span>
                        <strong>{contract.description}</strong>
                        <small>
                          {contract.kind === "monthly" ? "Mensal" : "Avulso"} · início{" "}
                          {contract.startOn.split("-").reverse().join("/")}
                        </small>
                      </span>
                      <span className="cx-pf-contract-right">
                        <b className="cx-data">{formatCents(contract.amountCents)}</b>
                        {contract.cancelledOn ? (
                          <Pill>Encerrado</Pill>
                        ) : contract.closedOn ? (
                          <Pill tone="good">Ativo</Pill>
                        ) : (
                          <Pill tone="warn">Aguardando 1º pagamento</Pill>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cx-pf-empty">
                  Nenhum contrato registrado. O histórico anterior não foi
                  transformado em pagamentos.
                </p>
              )}
            </Panel>

            <Panel
              title="Contatos"
              icon={<Users size={14} />}
              tone="var(--accent-2)"
              count={contacts.length}
              action={
                <Button
                  size="compact"
                  disabled={!contactsWritable}
                  title={
                    contactsWritable
                      ? undefined
                      : company
                        ? "A fundação de identidade não está habilitada neste ambiente."
                        : "Este cadastro ainda não tem empresa vinculada, e um contato pertence a uma empresa."
                  }
                  onClick={() => setContactFor({})}
                >
                  + Contato
                </Button>
              }
            >
              {contacts.length ? (
                <ul className="cx-pf-people">
                  {contacts.map((contact) => (
                    <li key={contact.id}>
                      <Avatar name={contact.name} />
                      <span>
                        <strong>{contact.name}</strong>
                        <small>
                          {[
                            contact.role,
                            contact.id === lead.primaryContactId
                              ? "Primário"
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "Dados ainda não informados"}
                        </small>
                      </span>
                      <Button
                        size="compact"
                        disabled={!contactsWritable}
                        title={
                          contactsWritable
                            ? undefined
                            : "A fundação de identidade não está habilitada neste ambiente."
                        }
                        onClick={() => setContactFor({ id: contact.id })}
                      >
                        Editar
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cx-pf-empty">
                  Nenhum contato adicional. Adicione as pessoas que participam da
                  decisão.
                </p>
              )}
            </Panel>

            <Panel
              title="Arquivos"
              icon={<Paperclip size={14} />}
              tone="var(--accent)"
              count={attachments.length + links.length}
              action={
                <Button
                  size="compact"
                  icon={<Plus size={14} aria-hidden="true" />}
                  onClick={() => setLinking(true)}
                >
                  Vincular
                </Button>
              }
            >
              {attachments.length + links.length ? (
                <ul className="cx-pf-files">
                  {attachments.map((file) => (
                    <li key={file.id}>
                      <span>
                        <strong>{file.name}</strong>
                        <small>
                          Arquivo privado · {Math.round(file.size / 1024)} kB
                        </small>
                      </span>
                      <IconButton
                        size="compact"
                        variant="bare"
                        label={`Remover ${file.name}`}
                        icon={<Trash2 size={14} aria-hidden="true" />}
                        onClick={() => {
                          void store.removeAttachment(file.id);
                        }}
                      />
                    </li>
                  ))}
                  {links.map((link) => (
                    <li key={link.id}>
                      <span>
                        <strong>{link.label}</strong>
                        <small>{link.url}</small>
                      </span>
                      <a
                        className="cx-btn"
                        data-variant="text"
                        data-size="compact"
                        href={link.url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        Abrir
                        <ExternalLink size={13} aria-hidden="true" />
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cx-pf-empty">
                  Nenhum documento vinculado. Adicione um link de proposta,
                  contrato ou material de apoio.
                </p>
              )}
            </Panel>

            <Panel
              title="Diagnóstico"
              icon={<Target size={14} />}
              tone="var(--warn)"
              className="cx-pf-diag"
            >
              {diagFields.length ? (
                <dl>
                  {diagFields.map((field) => (
                    <div key={field.id}>
                      <dt>{field.name}</dt>
                      <dd>
                        {String(fieldValue(field.id) ?? "") || "Não informado"}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="cx-pf-empty">
                  Este funil ainda não tem campos de diagnóstico. Dor, objeção e
                  entrega adicional são campos do funil, configurados em
                  Configurações.
                </p>
              )}
            </Panel>
          </div>
        </div>
      </section>

      {editing && membership && (
        <EditLeadDialog
          lead={lead}
          funnelId={membership.funnelId}
          onClose={() => setEditing(false)}
        />
      )}
      {losing && membership && (
        <LostDialog
          membershipId={membership.id}
          funnelId={membership.funnelId}
          onClose={() => setLosing(false)}
        />
      )}
      {noting && (
        <NoteDialog leadId={lead.id} onClose={() => setNoting(false)} />
      )}
      <NewTaskDialog
        open={tasking}
        progress="todo"
        fixedLeadId={lead.id}
        onClose={() => setTasking(false)}
      />
      {linking && (
        <LinkDialog leadId={lead.id} onClose={() => setLinking(false)} />
      )}
      {meeting && (
        <MeetingDialog
          leadId={lead.id}
          membershipId={membership?.id}
          onClose={() => setMeeting(false)}
        />
      )}
      {editandoReuniao && (
        <MeetingDialog
          leadId={lead.id}
          membershipId={membership?.id}
          meeting={editandoReuniao}
          onClose={() => setEditandoReuniao(undefined)}
        />
      )}
      {cancelandoReuniao && (
        <CancelMeetingDialog
          meeting={cancelandoReuniao}
          onClose={() => setCancelandoReuniao(undefined)}
        />
      )}
      {extraDe && (
        <ExtraInvoiceDialog contract={extraDe} onClose={() => setExtraDe(undefined)} />
      )}
      {encerrando && (
        <CancelContractDialog contract={encerrando} onClose={() => setEncerrando(undefined)} />
      )}
      {contracting && (
        <ContractDialog
          leadId={lead.id}
          membershipId={membership?.id}
          onClose={() => setContracting(false)}
        />
      )}
      {receiptFor && (
        <ReceiptDialog
          invoice={invoices.find((invoice) => invoice.id === receiptFor)!}
          contract={contracts.find(
            (contract) =>
              contract.id ===
              invoices.find((invoice) => invoice.id === receiptFor)?.contractId,
          )}
          onClose={() => setReceiptFor(null)}
        />
      )}
      {contactFor && (
        <ContactDialog
          lead={lead}
          contactId={contactFor.id}
          onClose={() => setContactFor(null)}
        />
      )}
      {trashing && (
        <ConfirmDialog
          title="Mover para a lixeira"
          question="O cadastro sai das listas ativas e pode ser restaurado com o histórico intacto."
          confirmLabel="Mover para a lixeira"
          danger
          onConfirm={() => {
            store.archiveLead(lead.id);
            showFeedback("Cadastro movido para a lixeira.", "success");
            navigate(appPath("leads"));
          }}
          onClose={() => setTrashing(false)}
        />
      )}
      {undoing && (
        <ConfirmDialog
          title="Desfazer registro"
          question={undoPlan(undoing, store.events, store.stages).question}
          confirmLabel="Desfazer"
          danger
          onConfirm={() => {
            store.deleteJourneyEvent(undoing.id);
            showFeedback("Registro desfeito.", "success");
          }}
          onClose={() => setUndoing(null)}
        />
      )}
    </div>
  );
}

export default PerfilPage;
